<template>
    <el-drawer v-model="innerVisible" size="65%" :with-header="true">
        <template #header>
            <div class="day-drawer-header">
                <div class="day-drawer-title">{{ drawerTitle }}</div>
                <div class="day-drawer-actions">
                    <div class="day-drawer-date-text">{{ dayDescriptor }}（{{ reportDate }}）</div>
                    <el-button text class="day-date-btn" @click="showCustomDate = !showCustomDate">
                        {{ showCustomDate ? '收起日期' : '自定义日期' }}
                    </el-button>
                    <el-date-picker v-if="showCustomDate" v-model="customDate" type="date" value-format="YYYY-MM-DD"
                        :disabled-date="disabledDate" placeholder="选择三个月内日期" class="day-date-picker" />
                    <el-button v-if="showCustomDate" type="primary" size="small" :disabled="!customDate"
                        @click="onQueryCustomDate" style="color: #fff;">
                        查询
                    </el-button>
                </div>
            </div>
        </template>
        <!-- 昨日日报/自定义日期日报 -->
        <div class="day-detail" v-loading="reportDetailLoading">
            <!-- 基本信息 -->
            <h3 class="day-section-title">基本信息</h3>
            <el-descriptions :column="4" border class="day-basic-table">
                <el-descriptions-item label="电站名称">
                    {{ stationInfo.name || '--' }}
                </el-descriptions-item>
                <el-descriptions-item label="并网地区">
                    {{ stationInfo.province || '--' }}
                </el-descriptions-item>
                <el-descriptions-item label="装机容量">
                    {{ formatNumber(stationInfo.capacity).value + formatNumber(stationInfo.capacity).unit || '--' }}
                </el-descriptions-item>
                <el-descriptions-item label="计费模式">
                    {{ formatBillingMode(stationInfo.billingMode) }}
                </el-descriptions-item>
                <el-descriptions-item label="投运日期">
                    {{ stationInfo.commossioningTime || '--' }}
                </el-descriptions-item>
                <el-descriptions-item label="运行时长">
                    {{ stationInfo.runningDay || '--' }}天
                </el-descriptions-item>
                <el-descriptions-item label="设备品牌">
                    {{ stationInfo.manufacturer || '奇点' }}
                </el-descriptions-item>
                <el-descriptions-item label="设备台数">
                    {{ stationInfo.eBlockNum || '--' }}台
                </el-descriptions-item>
            </el-descriptions>

            <!-- 当日收益卡片 -->
            <h3 class="day-section-title">{{ dayDescriptor }}收益</h3>
            <div class="day-cards-row">
                <div class="day-card">
                    <div class="day-card-label">{{ dayDescriptor }}收益</div>
                    <div class="day-card-value">
                        <span class="num">{{ formatMoney(dayIncomeNum).value }}</span>
                        <span class="unit">{{ formatMoney(dayIncomeNum).unit }}</span>
                    </div>
                </div>
                <div class="day-card">
                    <div class="day-card-label">{{ dayDescriptor }}充电量</div>
                    <div class="day-card-value">
                        <span class="num">{{ formatNumber(dayChargeNum).value  }}</span>
                        <span class="unit">{{ formatNumber(dayChargeNum).unit }} </span>
                    </div>
                </div>
                <div class="day-card">
                    <div class="day-card-label">{{ dayDescriptor }}放电量</div>
                    <div class="day-card-value">
                        <span class="num">{{ formatNumber(dayDischargeNum).value  }}</span>
                        <span class="unit">{{ formatNumber(dayDischargeNum).unit }} </span>
                    </div>
                </div>
            </div>

            <!-- 收益明细：periodList 时段汇总 + timeSharingList 分时明细，见 getDailySummary.data.daily[] -->
            <h3 class="day-section-title">{{ dayDescriptor }}收益明细</h3>
            <el-tabs v-if="incomeDetailMeterTabs.length" v-model="incomeDetailMeterTab" class="day-income-meter-tabs">
                <el-tab-pane v-for="tab in incomeDetailMeterTabs" :key="tab.key" :label="tab.label" :name="tab.name" />
            </el-tabs>
            <h4 class="day-income-subtitle">时段汇总 <span>（根据电表尖峰平谷时段电能汇总） </span></h4>
            <el-table :key="'period-' + incomeDetailMeterTab" :data="incomeDetailPeriod" border class="day-income-table"
                :span-method="incomePeriodSpanMethod">
                <el-table-column prop="incomeType" label="收益类型" width="80" />
                <el-table-column prop="priceType" label="电价类型" width="80" />
                <el-table-column prop="periodType" label="时段类型" width="80" />
                <!-- <el-table-column prop="timeRange" label="时段" width="100" /> -->
                <el-table-column prop="price" label="单价" width="100" />
                <el-table-column prop="direction" label="电能方向" width="100" />
                <el-table-column prop="startIndex" label="电表开始底数" width="140" align="right" />
                <el-table-column prop="endIndex" label="电表截止底数" width="140" align="right" />
                <el-table-column prop="multiplier" label="倍率" width="80" align="right" />
                <el-table-column prop="energy" label="电量" width="100" align="right" />
                <el-table-column prop="fee" label="电费" width="100" align="right" />
                <el-table-column prop="sum" label="合计" width="100" align="right" />
                <el-table-column prop="total" label="总计" align="right" />
            </el-table>
            <h4 class="day-income-subtitle">分时明细<span>（根据人为拆分小时时段电能汇总）</span></h4>
            <el-table :key="'sharing-' + incomeDetailMeterTab" :data="incomeDetailTimeSharing" border
                class="day-income-table" :span-method="incomeTimeSharingSpanMethod">
                <el-table-column prop="incomeType" label="收益类型" width="80" />
                <el-table-column prop="priceType" label="电价类型" width="80" />
                <el-table-column prop="periodType" label="时段类型" width="80" />
                <el-table-column prop="timeRange" label="时段" width="95" />
                <el-table-column prop="price" label="单价" width="90" align="right" />
                <el-table-column prop="direction" label="电能方向" width="90" />
                <el-table-column prop="startIndex" label="电表开始底数" width="110" align="right" />
                <el-table-column prop="endIndex" label="电表截止底数" width="110" align="right" />
                <el-table-column prop="multiplier" label="倍率" width="80" align="right" />
                <el-table-column prop="energy" label="电量" width="90" align="right" />
                <el-table-column prop="fee" label="电费" width="100" align="right" />
                <el-table-column prop="sum" label="合计" width="100" align="right" />
                <el-table-column prop="total" label="总计" align="right" />
            </el-table>

            <!-- 累计收益概览（接口 overview：sumPositive / sumReverse / sumIncome 等） -->
            <div class="day-summary-grid">
                <img src="@/assets/icon_leiji.png" alt="累计充电量" />

                    <div class="day-summary-card">

                        <div class="meta">
                            <div class="label">累计充电量</div>
                            <div class="value">
                                {{ overviewCumulativeCharge.value }}
                                <span v-if="overviewCumulativeCharge.unit" class="unit">{{ overviewCumulativeCharge.unit
                                    }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="day-summary-card">
                        <div class="meta">
                            <div class="label">累计放电量</div>
                            <div class="value">
                                {{ overviewCumulativeDischarge.value }}
                                <span v-if="overviewCumulativeDischarge.unit" class="unit">{{
                                    overviewCumulativeDischarge.unit
                                    }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="day-summary-card day-summary-card-sum-income">
                        <div class="meta">
                            <div class="label">累计收益</div>
                            <div class="value">
                                <span class="num">{{ formatMoney(overviewBlock.sumIncome).value }}</span>
                                <span class="unit">{{ formatMoney(overviewBlock.sumIncome).unit }}</span>
                            </div>
                        </div>
                    </div>
                
            </div>

            <div class="day-summary-grid">
                <img src="@/assets/icon_leiji2.png" alt="累计充电量" />
                
                    <div class="day-summary-card">
                        <div class="meta">
                            <div class="label">本月总收益（元）</div>
                            <div class="value">
                                <span class="num">{{ formatMoney(overviewBlock.thisMonthIncome).value }}</span>
                               <span class="unit">{{ formatMoney(overviewBlock.thisMonthIncome).unit }}</span>
                                <span v-if="overviewComparedLastMonthTrend" class="trend"
                                    :class="overviewComparedLastMonthTrend.cls">
                                    {{ overviewComparedLastMonthTrend.text }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="day-summary-card">
                        <div class="meta">
                            <div class="label">本月单位容量收益（元/kWh）</div>
                            <div class="value">
                                {{ overviewUnitCapacityIncomeDisplay }}
                                <span v-if="overviewInFrontTrend" class="trend up">{{ overviewInFrontTrend }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="day-summary-card">
                        <div class="meta">
                            <div class="label">本月日均循环次数均值（次）</div>
                            <div class="value">
                                {{ overviewAvgCycleDisplay }}
                                <span v-if="overviewCycleStandardTrend" class="trend"
                                    :class="overviewCycleStandardTrend.cls">
                                    {{ overviewCycleStandardTrend.text }}
                                </span>
                            </div>
                        </div>
                    </div>
               
            </div>

            <!-- 收益分析与统计（下半部分） -->
            <h3 class="day-section-title">收益分析</h3>

            <div class="day-analysis-row">
                <!-- 收益分布饼图 -->
                <div class="day-chart-card">
                    <div class="day-chart-title">累计收益分布</div>
                    <VChart class="day-pie-chart" :option="incomePieOption" autoresize />
                    <ul class="day-pie-legend">
                        <li v-for="item in incomePieLegendItems" :key="item.name">
                            <span class="dot" :style="{ background: item.color }"></span>
                            <span class="name">{{ item.name }}</span>
                            <span class="pct">{{ item.pct }}%</span>
                        </li>
                    </ul>
                </div>

                <!-- 收益统计柱状图 -->
                <div class="day-chart-card">
                    <div class="day-chart-title">本月收益统计</div>
                    <VChart class="day-bar-chart" :option="incomeBarOption" autoresize />
                </div>
            </div>

            <!-- 收益统计明细：getDailySummary → eBlockNameList + eBlockData，仅展示当前所选日期 -->
            <h3 class="day-section-title">{{ dayDescriptor }}充电电量汇总</h3>
            <el-table :data="incomeEBlockStatRows" border class="day-stat-table day-eblock-stat-table"
                :span-method="eBlockIncomeStatSpanMethod">
                <el-table-column prop="date" label="日期" width="120" fixed="left" align="center"
                    v-if="incomeEBlockStatRows.length > 0" />
                <el-table-column v-for="(blockName, bi) in eBlockStatBlockNames" :key="String(blockName) + '-' + bi"
                    :label="blockName" align="center">
                    <el-table-column :prop="'b' + bi + '_time'" label="充电时间" min-width="130" align="center" />
                    <el-table-column :prop="'b' + bi + '_energy'" label="充电量(kWh)" min-width="110" align="right" />
                    <el-table-column label="末端SOC(%)" min-width="100" align="center">
                        <template #default="{ row }">
                            <span class="eblock-soc-cell">{{ row['b' + bi + '_soc'] }}</span>
                        </template>
                    </el-table-column>
                </el-table-column>
            </el-table>

            <!-- 需量分析 -->
            <h3 class="day-section-title">{{ dayDescriptor }}需量分析</h3>
            <div class="day-chart-card day-demand-card">
                <div class="day-chart-title">
                    需量（kW）
                    <span class="day-chart-sub">需量最大值</span>
                </div>
                <VChart class="day-demand-chart" :option="demandLineOption" autoresize />
            </div>

            <!-- 设备告警 -->
            <h3 class="day-section-title">{{ dayDescriptor }}设备告警</h3>
            <el-table :data="deviceAlarms" border class="day-alarm-table" v-loading="deviceAlarmsLoading">
                <el-table-column prop="description" label="告警名称" />
                <el-table-column label="告警级别"  align="center">
                    <template #default="{ row }">
                        {{ formatAlarmLevel(row.level) }}
                    </template>
                </el-table-column>
                <!-- <el-table-column prop="time" label="触发时间" width="180" />
                <el-table-column prop="duration" label="持续时间" width="90" /> -->
                <el-table-column prop="triggerCount" label="告警次数"  />
            </el-table>
            <div class="day-alarm-pagination">
                <div class="pagination-container">
                    <div class="pagination-info">共 {{ deviceAlarmsTotal }} 项数据</div>
                    <div class="pagination-controls">

                        <v-pagination v-model="deviceAlarmsPage" :length="deviceAlarmsTotalPages" :total-visible="7"  rounded="circle" size="small"
                         @update:model-value="onDeviceAlarmsPageChange" />

                    </div>
                </div>
            </div>

            <!-- 优化建议 -->
            <!-- <h3 class="day-section-title">优化建议</h3>
            <ul class="day-advise-list">
                <li><span class="tag low">低风险</span> 设备告警持续时长较长</li>
                <li><span class="tag mid">中风险</span> 设备运行较频繁，影响收益类</li>
                <li><span class="tag high">高风险</span> 建议尽快检查设备，排除隐患</li>
            </ul> -->

            <!-- 风险预警与优化建议 -->
            <!-- <h3 class="day-section-title">风险预警与优化建议</h3>
            <div class="day-risk-card">
                <div class="day-risk-header">
                    <span class="tag low">低风险</span>
                    <span class="title">充放电效率短期小幅波动</span>
                </div>
                <p class="day-risk-desc">
                    特征数据结果：单日充放电效率从 93.7% 较昨日 92.5% 略有波动，短时高温（≤35℃）可能是主要原因。
                </p>
                <p class="day-risk-desc">
                    预警建议：检查温控及散热系统，保证通风良好，无需暂停机组运行。
                </p>
            </div> -->
        </div>
    </el-drawer>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
    getStationInfo,
    getOverview,
    getChargeSummary,
    queryLinesOption,
    getDailySummary,
    getChargeRecords,
    getAlertList
} from '@/api/business-operations/index.js'
import dayjs from 'dayjs'

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    row: {
        type: Object,
        default: () => ({})
    }
})

const emit = defineEmits(['update:visible'])

const innerVisible = computed({
    get: () => props.visible,
    set: (val) => emit('update:visible', val)
})

const row = computed(() => props.row || {})

// 默认：昨日；自定义日期：三个月内
const reportDate = ref(dayjs().subtract(1, 'day').format('YYYY-MM-DD'))
const customDate = ref(reportDate.value)
const showCustomDate = ref(false)
const reportDetailLoading = ref(false)

const dayDescriptor = computed(() => {
    const y = dayjs().subtract(1, 'day').format('YYYY-MM-DD')
    if (reportDate.value === y) return '昨日'
    return dayjs(reportDate.value).format('MM月DD日')
})

const drawerTitle = computed(() => `日报详情(${reportDate.value})`)

const disabledDate = (d) => {
    // 限制过去三个月（不含今天，只允许到“昨日”）
    const min = dayjs().subtract(3, 'month').startOf('day')
    const max = dayjs().subtract(1, 'day').endOf('day')
    const cur = dayjs(d)
    return cur.isBefore(min) || cur.isAfter(max)
}

const incomeDetailSample = []

/** 时段类型与后端 type 对应：1尖峰 3峰 4平 5谷 6深谷（无 type 2） */
const TOU_PERIOD_LABELS = ['尖峰', '峰', '平', '谷', '深谷']

/** 字符串别名 → 上表规范文案 */
const TOU_PERIOD_ALIAS = {
    尖峰: '尖峰',
    尖: '尖峰',
    峰: '峰',
    平: '平',
    平时段: '平',
    谷: '谷',
    深谷: '深谷',
    深: '深谷',
    低谷: '谷'
}

/** 数字 type → 展示文案（与后端约定一致） */
const TOU_TYPE_NUM_MAP = {
    1: '尖峰',
    3: '峰',
    4: '平',
    5: '谷',
    6: '深谷'
}

function touPeriodLabel(type) {
    if (type == null || type === '') return '--'

    if (typeof type === 'string') {
        const s = type.trim()
        if (TOU_PERIOD_LABELS.includes(s)) return s
        if (TOU_PERIOD_ALIAS[s]) return TOU_PERIOD_ALIAS[s]
        const num = Number(s)
        if (Number.isFinite(num) && TOU_TYPE_NUM_MAP[num]) return TOU_TYPE_NUM_MAP[num]
        return '--'
    }

    const t = Number(type)
    if (!Number.isFinite(t)) return '--'
    if (TOU_TYPE_NUM_MAP[t]) return TOU_TYPE_NUM_MAP[t]
    return '--'
}

function formatIncomeDetailNum(v) {
    if (v == null || v === '') return '--'
    if (typeof v === 'number' && !Number.isFinite(v)) return '--'
    const n = Number(v)
    if (Number.isNaN(n)) return String(v)
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 4 })
}

/**
 * 分时/时段项上的单价：多字段 + 当前电表 daily 回退 + 无单价时用电费/电量反推
 * （接口字段名不统一：price / unit_price / 或仅返回 income+energy）
 */
const UNIT_PRICE_KEYS = [
    'price',
    'unitPrice',
    'unit_price',
    'Price',
    'electricityPrice',
    'electricity_price',
    'elecPrice',
    'elec_price',
    'tariff',
    'tariffPrice',
    'dealPrice',
    'deal_price',
    'meterPrice',
    'meter_price'
]

function tryParseUnitPriceValue(v) {
    if (v == null) return null
    if (typeof v === 'string') {
        const s = v.trim()
        if (s === '' || s.toLowerCase() === 'null' || s === '--') return null
        // 支持：1,579.1 / 12.3元/kWh / 12.3 unit / 其他带千分位/单位字符串
        const cleaned = s.replace(/,/g, '')
        const m = cleaned.match(/-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/i)
        if (!m) return null
        const n = Number(m[0])
        return Number.isFinite(n) ? n : null
    }
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    if (typeof v === 'object') {
        if ('value' in v) return tryParseUnitPriceValue(v.value)
        if ('amount' in v) return tryParseUnitPriceValue(v.amount)
        if ('price' in v) return tryParseUnitPriceValue(v.price)
        if ('unitPrice' in v) return tryParseUnitPriceValue(v.unitPrice)
    }
    return null
}

/**
 * 分时/时段项上的电量：接口字段名不统一（Jackson 常见 ePositive/eReverse，历史代码只读 epositive/ereverse）
 * 按优先级取第一个「存在且能解析为数字」的字段，避免 epositive=0 盖住真实电量在 ePositive 的情况
 */
const POSITIVE_ENERGY_KEYS = [
    'ePositive',
    'epositive',
    'positiveEnergy',
    'positive_energy',
    'e_positive',
    'positiveElectric',
    'positive_electric',
    'chargeEnergy',
    'charge_energy'
]
const REVERSE_ENERGY_KEYS = [
    'eReverse',
    'ereverse',
    'reverseEnergy',
    'reverse_energy',
    'e_reverse',
    'reverseElectric',
    'reverse_electric',
    'dischargeEnergy',
    'discharge_energy'
]

function itemDirectionEnergyNumber(item, direction) {
    if (!item || typeof item !== 'object') return NaN
    const keys = direction === 'positive' ? POSITIVE_ENERGY_KEYS : REVERSE_ENERGY_KEYS

    // 业务口径：电量 = 截止读数 - 起始读数（当接口直接电量字段缺失/为 0 时回退）
    const diffFromReadings = () => {
        if (direction === 'positive') {
            const s = toFiniteNumber(item.positiveStart)
            const e = toFiniteNumber(item.positiveEnd)
            if (s == null || e == null) return NaN
            return e - s
        }
        const s = toFiniteNumber(item.reverseStart)
        const e = toFiniteNumber(item.reverseEnd)
        if (s == null || e == null) return NaN
        return e - s
    }

    let energyFromField = null
    for (const k of keys) {
        if (!(k in item)) continue
        const n = toFiniteNumber(item[k])
        if (n != null) {
            energyFromField = n
            break
        }
    }
    if (energyFromField != null) {
        // 如果接口电量为 0，但读数差能算出非 0，则以读数差为准
        if (energyFromField === 0) {
            const d = diffFromReadings()
            if (Number.isFinite(d) && d !== 0) return d
        }
        return energyFromField
    }

    const re =
        direction === 'positive'
            ? /^(positive_?(energy|electric|quantity)|charge_?energy)$/i
            : /^(reverse_?(energy|electric|quantity)|discharge_?energy)$/i
    for (const k of Object.keys(item)) {
        if (!re.test(k)) continue
        const n = toFiniteNumber(item[k])
        if (n != null) {
            if (n === 0) {
                const d = diffFromReadings()
                if (Number.isFinite(d) && d !== 0) return d
            }
            return n
        }
    }

    const d = diffFromReadings()
    return Number.isFinite(d) ? d : NaN
}

/** 仅有电费+电量时反推单价（与接口缺 price 时兜底） */
function inferUnitPriceFromFeeEnergy(item) {
    if (!item || typeof item !== 'object') return null
    const pf = toFiniteNumber(item.positiveIncome)
    const ef = itemDirectionEnergyNumber(item, 'positive')
    if (pf != null && Number.isFinite(ef) && Math.abs(ef) > 1e-9) return pf / ef
    const rf = toFiniteNumber(item.reverseIncome)
    const er = itemDirectionEnergyNumber(item, 'reverse')
    if (rf != null && Number.isFinite(er) && Math.abs(er) > 1e-9) return rf / er
    return null
}

function resolveItemUnitPriceRaw(item, meter) {
    const scan = (obj) => {
        if (!obj || typeof obj !== 'object') return null
        const pr = obj.price
        if (pr != null && typeof pr === 'object' && pr !== null && 'value' in pr) {
            const parsed = tryParseUnitPriceValue(pr.value)
            if (parsed != null) return parsed
        }
        for (const k of UNIT_PRICE_KEYS) {
            if (!(k in obj)) continue
            const parsed = tryParseUnitPriceValue(obj[k])
            if (parsed != null) return parsed
        }
        for (const k of Object.keys(obj)) {
            if (!/price|tariff|电价|单价|unit/i.test(k)) continue
            if (k === 'priceType' || k === 'price_type') continue
            const parsed = tryParseUnitPriceValue(obj[k])
            if (parsed != null) return parsed
        }
        return null
    }

    const fromItem = scan(item)
    if (fromItem != null) return fromItem
    const fromMeter = scan(meter)
    if (fromMeter != null) return fromMeter

    const inferred = inferUnitPriceFromFeeEnergy(item)
    if (inferred != null && Number.isFinite(inferred)) return inferred

    return null
}

function getItemUnitPriceNumber(item, meter) {
    const raw = resolveItemUnitPriceRaw(item, meter)
    if (raw == null) return NaN
    const n = toFiniteNumber(raw)
    return n != null && Number.isFinite(n) ? n : NaN
}

/** 仅用于金额/汇总：无效则 null */
function toFiniteNumber(v) {
    if (v == null || v === '') return null
    if (typeof v === 'string') {
        const s = v.trim()
        if (s === '' || s.toLowerCase() === 'null' || s === '--') return null
        const cleaned = s.replace(/,/g, '')
        const m = cleaned.match(/-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/i)
        if (!m) return null
        const n = Number(m[0])
        return Number.isFinite(n) ? n : null
    }
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

function createSampleSpanMeta(totalRows) {
    return {
        totalRows,
        periodCell(rowIndex) {
            if (rowIndex === 0) return { rowspan: totalRows, colspan: 1 }
            return { rowspan: 0, colspan: 0 }
        },
        timePriceCell(rowIndex) {
            if (rowIndex % 2 === 0) return { rowspan: 2, colspan: 1 }
            return { rowspan: 0, colspan: 0 }
        },
        sumCell(rowIndex) {
            if (rowIndex % 2 === 0) return { rowspan: 2, colspan: 1 }
            return { rowspan: 0, colspan: 0 }
        },
        totalCell(rowIndex) {
            if (rowIndex === 0) return { rowspan: totalRows, colspan: 1 }
            return { rowspan: 0, colspan: 0 }
        }
    }
}

/**
 * 倍率：优先本条时段（periodList / timeSharingList 单项），再回落到当前电表 daily[i]
 * 兼容 product_ratio、倍率为 0 的合法情况
 */
function resolveProductRatioRaw(meter, item) {
    for (const v of [item?.productRatio, item?.product_ratio, meter?.productRatio, meter?.product_ratio]) {
        if (v != null && v !== '') return v
        if (v === 0 || v === '0') return v
    }
    return null
}

function formatProductRatioCell(meter, item) {
    const raw = resolveProductRatioRaw(meter, item)
    if (raw == null || raw === '') return '--'
    return formatIncomeDetailNum(raw)
}

/**
 * 计算用倍率：展示为「--」时（无倍率）按 1 参与运算，避免合计/总计被 NaN 或 0 误伤
 * 明确为 0 的倍率仍按 0 计算
 */
function effectiveProductRatioForCalc(meter, item) {
    const raw = resolveProductRatioRaw(meter, item)
    if (raw == null || raw === '') return 1
    const n = Number(raw)
    if (!Number.isFinite(n)) return 1
    return n
}

function slotEnergySum(item) {
    const a = itemDirectionEnergyNumber(item, 'positive')
    const b = itemDirectionEnergyNumber(item, 'reverse')
    const fa = Number.isFinite(a) ? a : 0
    const fb = Number.isFinite(b) ? b : 0
    return fa + fb
}

/**
 * 单向电费：优先接口字段 positiveIncome / reverseIncome；
 * 缺失时回落为 单价×电量×有效倍率（无倍率时倍率=1）
 */
function slotDirectionFeeAmount(meter, item, direction) {
    const feeRaw =
        direction === 'positive'
            ? item.positiveIncome
            : direction === 'reverse'
                ? item.reverseIncome
                : null
    const fee = toFiniteNumber(feeRaw)
    if (fee != null) return fee

    const ratioEff = effectiveProductRatioForCalc(meter, item)
    const p = getItemUnitPriceNumber(item, meter)
    const e =
        direction === 'positive'
            ? itemDirectionEnergyNumber(item, 'positive')
            : direction === 'reverse'
                ? itemDirectionEnergyNumber(item, 'reverse')
                : NaN
    if (!Number.isFinite(p) || !Number.isFinite(e)) return null
    return p * e * ratioEff
}

/** 时段合计：反向电费 - 正向电费 */
function slotSubtotalAmount(meter, item) {
    const pos = slotDirectionFeeAmount(meter, item, 'positive')
    const rev = slotDirectionFeeAmount(meter, item, 'reverse')
    if (pos == null && rev == null) return null
    return (rev ?? 0) - (pos ?? 0)
}

function formatSlotTotalIncome(meter, item) {
    const amt = slotSubtotalAmount(meter, item)
    if (amt != null && Number.isFinite(amt)) return formatIncomeDetailNum(amt)
    return '--'
}

/** 时段汇总合并后：多段用中文逗号拼接 */
const PERIOD_SUMMARY_TYPE_ORDER = ['尖峰', '峰', '平', '谷', '深谷']

/**
 * 时段汇总（periodList）：按尖峰/峰/平/谷/深谷合并多段，电量与电费、合计等为求和；单价为电量加权平均
 */
function aggregatePeriodListByTouType(periodList, meter) {
    const groups = new Map()
    for (const it of periodList) {
        let label = touPeriodLabel(it.type)
        if (label === '--') label = '其它'
        if (!groups.has(label)) groups.set(label, [])
        groups.get(label).push(it)
    }

    const merged = []
    const pushMerged = (label, items) => {
        if (items?.length) merged.push(buildMergedPeriodGroupRow(label, items, meter))
    }

    for (const label of PERIOD_SUMMARY_TYPE_ORDER) {
        if (groups.has(label)) {
            pushMerged(label, groups.get(label))
            groups.delete(label)
        }
    }
    for (const [label, items] of groups) {
        pushMerged(label, items)
    }
    return merged
}

function buildMergedPeriodGroupRow(periodTypeLabel, items, meter) {
    const ranges = items
        .map((it) =>
            it.beginTime != null && it.endTime != null ? `${it.beginTime}-${it.endTime}` : null
        )
        .filter(Boolean)
    const _mergedTimeDisplay = ranges.length ? ranges.join('，') : '--'

    let sumEpos = 0
    let sumErev = 0
    let sumPosFee = 0
    let sumRevFee = 0
    let hasPosFee = false
    let hasRevFee = false
    let priceW = 0
    let energyW = 0
    let firstFinitePrice = null
    let priceSum = 0
    let priceCount = 0
    let subtotalSum = 0
    let hasSubtotal = false

    for (const it of items) {
        const epos = itemDirectionEnergyNumber(it, 'positive')
        const erev = itemDirectionEnergyNumber(it, 'reverse')
        if (Number.isFinite(epos)) sumEpos += epos
        if (Number.isFinite(erev)) sumErev += erev

        const pf = toFiniteNumber(it.positiveIncome)
        if (pf != null) {
            sumPosFee += pf
            hasPosFee = true
        }
        const rf = toFiniteNumber(it.reverseIncome)
        if (rf != null) {
            sumRevFee += rf
            hasRevFee = true
        }

        const p = getItemUnitPriceNumber(it, meter)
        const e = slotEnergySum(it)
        if (Number.isFinite(p)) {
            if (firstFinitePrice == null) firstFinitePrice = p
            priceSum += p
            priceCount += 1
            // 仅在能获得“有效电量”时才做加权平均
            if (Number.isFinite(e) && e !== 0) {
                priceW += p * e
                energyW += e
            }
        }

        const sub = slotSubtotalAmount(meter, it)
        if (sub != null && Number.isFinite(sub)) {
            subtotalSum += sub
            hasSubtotal = true
        }
    }

    const first = items[0]
    const last = items[items.length - 1]
    const weightedPrice = energyW > 0 ? priceW / energyW : null
    let finalPrice = weightedPrice != null && Number.isFinite(weightedPrice) ? weightedPrice : null
    // 当电量字段在接口上为 null/0 导致无法加权时，至少保证“单价”不会丢失
    if (finalPrice == null && firstFinitePrice != null) finalPrice = firstFinitePrice
    if (finalPrice == null && priceCount > 0) finalPrice = priceSum / priceCount

    return {
        type: first.type,
        positiveStart: first.positiveStart,
        positiveEnd: last.positiveEnd,
        reverseStart: first.reverseStart,
        reverseEnd: last.reverseEnd,
        epositive: sumEpos,
        ereverse: sumErev,
        positiveIncome: hasPosFee ? sumPosFee : null,
        reverseIncome: hasRevFee ? sumRevFee : null,
        price: finalPrice != null && Number.isFinite(finalPrice) ? finalPrice : null,
        income: hasSubtotal ? subtotalSum : null,
        _mergedTimeDisplay,
        _periodTypeLabel: periodTypeLabel
    }
}

/** 时段列：分时为 beginTime 前 5 位；时段汇总为 begin-end；合并行为 _mergedTimeDisplay */
function formatIncomeTimeRangeCell(item, source) {
    if (item._mergedTimeDisplay) return item._mergedTimeDisplay
    if (source === 'timeSharing') {
        const b = item.beginTime
        return b != null && b !== '' ? String(b).slice(0, 5) : '--'
    }
    if (item.beginTime != null && item.endTime != null) return `${item.beginTime}-${item.endTime}`
    return '--'
}

/** 连续相同「时段类型」的合并段（每段含若干「时段对」，每对占 2 行） */
function buildPeriodTypeRuns(periodLabels) {
    const pairCount = periodLabels.length
    const runStarts = []
    for (let i = 0; i < pairCount; i++) {
        if (i === 0 || periodLabels[i] !== periodLabels[i - 1]) runStarts.push(i)
    }
    const runLengths = runStarts.map((start, i) => {
        const nextStart = runStarts[i + 1] ?? pairCount
        return nextStart - start
    })
    return { runStarts, runLengths }
}

function makeIncomeSpanMeta(totalRows, { runStarts, runLengths }) {
    return {
        totalRows,
        periodCell(rowIndex) {
            const pairIdx = Math.floor(rowIndex / 2)
            for (let r = 0; r < runStarts.length; r++) {
                const startPair = runStarts[r]
                const len = runLengths[r]
                if (pairIdx >= startPair && pairIdx < startPair + len) {
                    const firstRow = startPair * 2
                    return rowIndex === firstRow
                        ? { rowspan: len * 2, colspan: 1 }
                        : { rowspan: 0, colspan: 0 }
                }
            }
            return { rowspan: 1, colspan: 1 }
        },
        timePriceCell(rowIndex) {
            return rowIndex % 2 === 0 ? { rowspan: 2, colspan: 1 } : { rowspan: 0, colspan: 0 }
        },
        sumCell(rowIndex) {
            return rowIndex % 2 === 0 ? { rowspan: 2, colspan: 1 } : { rowspan: 0, colspan: 0 }
        },
        totalCell(rowIndex) {
            return rowIndex === 0 ? { rowspan: totalRows, colspan: 1 } : { rowspan: 0, colspan: 0 }
        }
    }
}

/** 每个时段 2 行：正向 / 反向 */
function appendIncomeDetailSlotRows(rows, { item, meter, periodType, timeRange, priceStr, slotIncomeStr, ratioStr }) {
    const base = {
        incomeType: '峰谷价差',
        priceType: '基本电价',
        periodType,
        timeRange,
        price: priceStr,
        multiplier: ratioStr
    }
    rows.push(
        {
            ...base,
            direction: '正向有功',
            startIndex: formatIncomeDetailNum(item.positiveStart),
            endIndex: formatIncomeDetailNum(item.positiveEnd),
            energy: formatIncomeDetailNum(itemDirectionEnergyNumber(item, 'positive')),
            fee: formatIncomeDetailNum(item.positiveIncome),
            sum: slotIncomeStr,
            total: ''
        },
        {
            ...base,
            direction: '反向有功',
            startIndex: formatIncomeDetailNum(item.reverseStart),
            endIndex: formatIncomeDetailNum(item.reverseEnd),
            energy: formatIncomeDetailNum(itemDirectionEnergyNumber(item, 'reverse')),
            fee: formatIncomeDetailNum(item.reverseIncome),
            sum: '',
            total: ''
        }
    )
}

function resolveIncomeGrandTotalFormatted(meter, summary, timeList) {
    let sum = 0
    let has = false
    for (const it of timeList) {
        const v = slotSubtotalAmount(meter, it)
        if (v != null && Number.isFinite(v)) {
            sum += v
            has = true
        }
    }
    return has ? formatIncomeDetailNum(sum) : '--'
}

function clampMeterTabIndex(tabKey, dailyLength) {
    let idx = Number(tabKey)
    if (!Number.isFinite(idx) || idx < 0) idx = 0
    if (idx >= dailyLength) idx = Math.max(0, dailyLength - 1)
    return idx
}

function getMeterTimeSeries(meter, source) {
    if (source === 'period') {
        return Array.isArray(meter.periodList) && meter.periodList.length ? meter.periodList : []
    }
    return Array.isArray(meter.timeSharingList) && meter.timeSharingList.length ? meter.timeSharingList : []
}

/**
 * periodList / timeSharingList → 表格行 + 合并元数据
 * @param {'period'|'timeSharing'} source
 */
function buildIncomeDetailModelFromList(summary, meterTabKey = '0', source = 'timeSharing') {
    const empty = { rows: [], meta: null, meterName: '' }
    const daily = summary?.daily

    if (!Array.isArray(daily) || !daily.length) {
        const rows = [...incomeDetailSample]
        const meta = createSampleSpanMeta(rows.length)
        // rows[0].total = '--'
        return { rows, meta, meterName: '' }
    }

    const idx = clampMeterTabIndex(meterTabKey, daily.length)
    const meter = daily[idx]
    const meterName = meter.meterName ?? meter.metername ?? ''
    const rawSeries = getMeterTimeSeries(meter, source)
    if (!rawSeries.length) return { ...empty, meterName }

    // 时段汇总：按尖峰/峰/平/谷/深谷合并；分时明细仍用原始 24 段
    const timeList =
        source === 'period' ? aggregatePeriodListByTouType(rawSeries, meter) : rawSeries

    const periodLabels = timeList.map((it) => it._periodTypeLabel ?? touPeriodLabel(it.type))
    const runs = buildPeriodTypeRuns(periodLabels)
    const rows = []

    for (let i = 0; i < timeList.length; i++) {
        const item = timeList[i]
        appendIncomeDetailSlotRows(rows, {
            item,
            meter,
            periodType: periodLabels[i],
            timeRange: formatIncomeTimeRangeCell(item, source),
            priceStr: (() => {
                // 优先直接使用合并行计算出的 item.price，避免二次解析链路吞掉数据
                const direct = item?.price
                const directNum = toFiniteNumber(direct)
                if (directNum != null) return formatIncomeDetailNum(directNum)

                const raw = resolveItemUnitPriceRaw(item, meter)
                return raw != null ? formatIncomeDetailNum(raw) : '--'
            })(),
            slotIncomeStr: formatSlotTotalIncome(meter, item),
            ratioStr: formatProductRatioCell(meter, item)
        })
    }

    rows[0].total = resolveIncomeGrandTotalFormatted(meter, summary, timeList)
    return {
        rows,
        meta: makeIncomeSpanMeta(rows.length, runs),
        meterName
    }
}

/** 与模板中 el-table-column 顺序一致（注意：period 表格少一列 timeRange，因此列索引不同） */
const INCOME_DETAIL_COL_PERIOD = {
    REVENUE_TYPE: 0,
    PRICE_CATEGORY: 1,
    PERIOD_TYPE: 2,
    UNIT_PRICE: 3, // period 表格：第 3 列是 price(单价)
    DIRECTION: 4,
    METER_START: 5,
    METER_END: 6,
    MULTIPLIER: 7,
    ENERGY: 8,
    FEE: 9,
    SUBTOTAL: 10, // period 表格：合计
    GRAND_TOTAL: 11 // period 表格：总计
}

const INCOME_DETAIL_COL_SHARING = {
    REVENUE_TYPE: 0,
    PRICE_CATEGORY: 1,
    PERIOD_TYPE: 2,
    TIME_SLOT: 3,
    UNIT_PRICE: 4,
    DIRECTION: 5,
    METER_START: 6,
    METER_END: 7,
    MULTIPLIER: 8,
    ENERGY: 9,
    FEE: 10,
    SUBTOTAL: 11,
    GRAND_TOTAL: 12
}

function applyIncomeDetailSpanMethod(model, { rowIndex, columnIndex }, source) {
    const { rows, meta } = model
    if (!rows?.length || !meta) return { rowspan: 1, colspan: 1 }

    const C = source === 'period' ? INCOME_DETAIL_COL_PERIOD : INCOME_DETAIL_COL_SHARING
    if (columnIndex === C.REVENUE_TYPE || columnIndex === C.PRICE_CATEGORY) {
        return rowIndex === 0 ? { rowspan: meta.totalRows, colspan: 1 } : { rowspan: 0, colspan: 0 }
    }
    if (columnIndex === C.PERIOD_TYPE) return meta.periodCell(rowIndex)
    if (source === 'period') {
        // period 表格：只需要合并“单价”(UNIT_PRICE 列)，不要影响“电能方向”
        if (columnIndex === C.UNIT_PRICE) return meta.timePriceCell(rowIndex)
    } else {
        // timeSharing 表格：需要合并“时段”(TIME_SLOT) 与 “单价”(UNIT_PRICE)
        if (columnIndex === C.TIME_SLOT || columnIndex === C.UNIT_PRICE) return meta.timePriceCell(rowIndex)
    }
    if (columnIndex === C.SUBTOTAL) return meta.sumCell(rowIndex)
    if (columnIndex === C.GRAND_TOTAL) return meta.totalCell(rowIndex)
    return { rowspan: 1, colspan: 1 }
}

/** 须在收益明细相关 computed 之前声明，避免 TDZ：Cannot access before initialization */
const dailySummaryData = ref({})

/** 并网表 Tab：与 getDailySummary 返回的 daily 一一对应 */
const incomeDetailMeterTab = ref('0')

const incomeDetailPeriodModel = computed(() =>
    buildIncomeDetailModelFromList(dailySummaryData.value, incomeDetailMeterTab.value, 'period')
)
const incomeDetailTimeSharingModel = computed(() =>
    buildIncomeDetailModelFromList(dailySummaryData.value, incomeDetailMeterTab.value, 'timeSharing')
)

const incomeDetailPeriod = computed(() => incomeDetailPeriodModel.value.rows)
const incomeDetailTimeSharing = computed(() => incomeDetailTimeSharingModel.value.rows)

const incomeDetailMeterTabs = computed(() => {
    const daily = dailySummaryData.value?.daily
    if (!Array.isArray(daily) || daily.length === 0) return []
    return daily.map((m, i) => {
        const raw = (m.meterName ?? m.metername ?? '').trim()
        const label = raw || `并网表 ${i + 1}`
        const key = String(m.uuid ?? m.meterId ?? i)
        return { key, label, name: String(i) }
    })
})

watch(
    incomeDetailMeterTabs,
    (tabs) => {
        if (!tabs.length) return
        const ok = tabs.some((t) => t.name === incomeDetailMeterTab.value)
        if (!ok) incomeDetailMeterTab.value = tabs[0].name
    },
    { deep: true }
)

function incomePeriodSpanMethod(payload) {
    return applyIncomeDetailSpanMethod(incomeDetailPeriodModel.value, payload, 'period')
}

function incomeTimeSharingSpanMethod(payload) {
    return applyIncomeDetailSpanMethod(incomeDetailTimeSharingModel.value, payload, 'timeSharing')
}

/** 需量曲线原始数据（getDataList：actualDemand / energyDemand / loadDemand） */
const linesOptionData = ref({
    actualDemand: [],
    energyDemand: [],
    loadDemand: [],
    cycle: null,
    slip: null
})

const DEMAND_LINE_COLORS = ['#3A68FF', '#16a34a', '#f59e0b', '#a855f7', '#06b6d4', '#ef4444']

/** 从 actualDemand / energyDemand / loadDemand 收集所有 record_time 并排序，作统一 X 轴 */
function pickDemandPointTime(p) {
    if (!p || typeof p !== 'object') return null
    return p?.record_time ?? p?.recordTime ?? p?.actual_time ?? p?.actualTime ?? p?.time ?? p?.timestamp ?? null
}

function pickDemandPointValue(p) {
    if (!p || typeof p !== 'object') return NaN
    const raw = p?.data ?? p?.value ?? p?.demand ?? p?.demandValue ?? p?.demand_value ?? null
    // 有些接口可能是 [time, value] 结构
    if (Array.isArray(raw)) {
        if (raw.length >= 2) return Number(raw[1])
        return Number(raw[0])
    }
    if (raw == null || raw === '') return NaN
    return Number(raw)
}

function toMsTimestamp(t) {
    if (t == null || t === '') return NaN
    const v = typeof t === 'number' ? t : String(t)
    const isDigits = typeof v === 'number' || (/^\d+$/.test(v))
    if (isDigits) {
        const ts = Number(v)
        // 2000-01-01 00:00:00 的毫秒时间戳：用于区分秒/毫秒
        const isMillisecond = ts > 946684800000
        return isMillisecond ? ts : ts * 1000
    }
    return dayjs(t).valueOf()
}

function formatHHmm(t) {
    const ms = toMsTimestamp(t)
    if (!Number.isFinite(ms)) return String(t)
    return dayjs(ms).format('HH:mm')
}

function collectDemandRecordTimes(payload) {
    const set = new Set()
    const scanList = (arr) => {
        if (!Array.isArray(arr)) return
        for (const line of arr) {
            const list = line?.demandList ?? line?.demand_list ?? []
            for (const p of list) {
                const t = pickDemandPointTime(p)
                if (t !== null && t !== undefined && t !== '') set.add(String(t))
            }
        }
    }
    scanList(payload.actualDemand)
    scanList(payload.energyDemand)
    scanList(payload.loadDemand)
    return Array.from(set).sort((a, b) => toMsTimestamp(a) - toMsTimestamp(b))
}

/** 将各条 line（lineName + demandList）转为与 xSorted 对齐的折线 series */
function buildDemandLineSeries(payload, xSorted) {
    const lines = [
        ...(Array.isArray(payload.actualDemand) ? payload.actualDemand : []),
        ...(Array.isArray(payload.energyDemand) ? payload.energyDemand : []),
        ...(Array.isArray(payload.loadDemand) ? payload.loadDemand : [])
    ]
    const series = []
    let ci = 0
    for (const line of lines) {
        const list = line?.demandList ?? line?.demand_list ?? []
        if (!list.length) continue
        const name = line?.lineName ?? line?.line_name ?? `需量${series.length + 1}`
        const map = new Map()
        for (const p of list) {
            const tk = pickDemandPointTime(p)
            if (tk === null || tk === undefined || tk === '') continue
            const v = pickDemandPointValue(p)
            map.set(String(tk), Number.isFinite(v) ? v : NaN)
        }
        const data = xSorted.map((k) => {
            const v = map.get(k)
            return v != null && Number.isFinite(v) ? v : null
        })
        const color = DEMAND_LINE_COLORS[ci % DEMAND_LINE_COLORS.length]
        ci += 1
        series.push({
            name,
            type: 'line',
            data,
            smooth: true,
            connectNulls: true,
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 4,
            lineStyle: { width: 2, color },
            itemStyle: { color },
            emphasis: { focus: 'series' }
        })
    }
    return series
}

// 需量分析：queryLinesOption → actualDemand / energyDemand / loadDemand，每条 lineName 一条曲线
const demandLineOption = computed(() => {
    const raw = linesOptionData.value
    const xSorted = collectDemandRecordTimes(raw)
    const series = buildDemandLineSeries(raw, xSorted)
    const xLabels = xSorted.map((t) => formatHHmm(t))

    if (!series.length) {
        return {
            tooltip: { trigger: 'axis' },
            legend: { show: false },
            grid: { top: 36, left: 48, right: 16, bottom: 28, containLabel: true },
            xAxis: { type: 'category', data: [] },
            yAxis: { type: 'value', name: 'kW' },
            series: []
        }
    }

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                snap: true,
                // 禁止 ECharts 默认的指针值展示（有时会取到不对应系列的值）
                label: { show: false },
            },
            formatter(params) {
                if (!Array.isArray(params) || !params.length) return ''
                const head = params[0].axisValue ?? ''
                const lines = params
                    .map((p) => {
                        const v = p.value
                        const s =
                            v != null && v !== '' && Number.isFinite(Number(v))
                                ? Number(v)
                                : '--'
                        return `${p.marker} ${p.seriesName}：${s} kW`
                    })
                    .join('<br/>')
                return `${head}<br/>${lines}`
            }
        },
        legend: {
            data: series.map((s) => s.name),
            top: 0,
            left: 'center',
            orient: 'horizontal',
            type: 'scroll',
            // 放到图表最上方：配合 grid.top 预留空间，避免占用折线区域
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 14,
            textStyle: { fontSize: 12 }
        },
        grid: { top: 64, left: 52, right: 16, bottom: xLabels.length > 60 ? 40 : 28, containLabel: true },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xLabels,
            axisLabel: { fontSize: 10, rotate: xLabels.length > 48 ? 45 : 0 }
        },
        yAxis: {
            type: 'value',
            name: 'kW',
            splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } }
        },
        series
    }
})

// 设备告警列表
/** 接口 level：0 提示 / 1 告警 / 2 停机故障 / 3 严重故障 */
const ALARM_LEVEL_LABEL = Object.freeze({
    0: '提示',
    1: '告警',
    2: '停机故障',
    3: '严重故障'
})

function formatAlarmLevel(level) {
    if (level === null || level === undefined || level === '') return '--'
    const n = Number(level)
    if (!Number.isNaN(n) && Object.prototype.hasOwnProperty.call(ALARM_LEVEL_LABEL, n)) {
        return ALARM_LEVEL_LABEL[n]
    }
    return String(level)
}

const deviceAlarms = ref([])
const deviceAlarmsTotal = ref(0)
const deviceAlarmsPage = ref(1)
const deviceAlarmsPageSize = ref(10)
const deviceAlarmsLoading = ref(false)
/** 接口一次返回全量数组时前端切片；为 null 表示服务端分页 */
const deviceAlarmsClientFullList = ref(null)

// 分页（参考 ChargeRecord.vue：下拉页大小 + v-pagination + 跳至页）
const pageSizeItems = [
    { title: '10 条/页', value: 10 },
    { title: '20 条/页', value: 20 },
    { title: '50 条/页', value: 50 },
    { title: '100 条/页', value: 100 }
]
const deviceAlarmsJumpPage = ref(1)
const deviceAlarmsTotalPages = computed(() => {
    const pages = Math.ceil(deviceAlarmsTotal.value / deviceAlarmsPageSize.value)
    return pages === 0 ? 1 : pages
})

function onDeviceAlarmsJumpToPage() {
    const p = Number(deviceAlarmsJumpPage.value)
    const clamped = Math.min(Math.max(1, p), deviceAlarmsTotalPages.value)
    deviceAlarmsJumpPage.value = clamped
    onDeviceAlarmsPageChange(clamped)
}

watch(deviceAlarmsTotalPages, (tp) => {
    if (deviceAlarmsJumpPage.value > tp) deviceAlarmsJumpPage.value = tp
})

function sliceDeviceAlarmsFromClientCache() {
    const full = deviceAlarmsClientFullList.value
    if (!full || !Array.isArray(full)) return
    const page = deviceAlarmsPage.value
    const size = deviceAlarmsPageSize.value
    const start = (page - 1) * size
    deviceAlarms.value = full.slice(start, start + size)
}

/**
 * 告警分页：请求带 pageNum、pageSize（兼容 page）
 * - data 为数组：视为全量，前端分页
 * - data 为 { list|records|rows, total|totalCount|... }：服务端分页
 */
const fetchAlertList = async (stationId, date, pageOverride, pageSizeOverride) => {
    if (!stationId) return
    const page = pageOverride ?? deviceAlarmsPage.value
    const pageSize = pageSizeOverride ?? deviceAlarmsPageSize.value

    deviceAlarmsLoading.value = true
    try {
        const params = {
            ...stationDateParams(stationId, date),
            pageNum: page,
            pageSize: pageSize,
            page
        }
        const res = await getAlertList(params)
        const raw = res?.rows

        if (Array.isArray(raw)) {
            deviceAlarmsClientFullList.value = raw
            deviceAlarmsTotal.value = raw.length
            const start = (page - 1) * pageSize
            deviceAlarms.value = raw.slice(start, start + pageSize)
            return
        }

        if (raw && typeof raw === 'object') {
            deviceAlarmsClientFullList.value = null
            const list = raw.list ?? raw.records ?? raw.rows ?? raw.dataList ?? []
            deviceAlarms.value = Array.isArray(list) ? list : []
            const t = res.total
            deviceAlarmsTotal.value =
                t != null && t !== '' ? Number(t) || 0 : deviceAlarms.value.length
            return
        }

        deviceAlarmsClientFullList.value = null
        deviceAlarms.value = []
        deviceAlarmsTotal.value = 0
    } catch {
        deviceAlarmsClientFullList.value = null
        deviceAlarms.value = []
        deviceAlarmsTotal.value = 0
    } finally {
        deviceAlarmsLoading.value = false
    }
}

function onDeviceAlarmsPageChange(newPage) {
    deviceAlarmsPage.value = newPage
    const stationId = props.row?.stationId
    const date = reportDate.value
    if (!stationId) return
    if (deviceAlarmsClientFullList.value) {
        sliceDeviceAlarmsFromClientCache()
    } else {
        fetchAlertList(stationId, date, newPage)
    }
}

function onDeviceAlarmsPageSizeChange(newSize) {
    deviceAlarmsPageSize.value = newSize
    deviceAlarmsPage.value = 1
    const stationId = props.row?.stationId
    const date = reportDate.value
    if (!stationId) return
    if (deviceAlarmsClientFullList.value) {
        sliceDeviceAlarmsFromClientCache()
    } else {
        fetchAlertList(stationId, date, 1, newSize)
    }
}



// 计费模式字典：0未知 1容量模式 2需量模式
const BILLING_MODE_LABEL = Object.freeze({
    0: '未知',
    1: '容量模式',
    2: '需量模式'
})

function formatBillingMode(mode) {
    if (mode === null || mode === undefined || mode === '') return '--'
    const n = Number(mode)
    if (!Number.isNaN(n) && Object.prototype.hasOwnProperty.call(BILLING_MODE_LABEL, n)) {
        return BILLING_MODE_LABEL[n]
    }
    return String(mode)
}

const stationInfo = ref({})
const lastLoadedStationId = ref(null)
// 获取基础信息
const fetchStationInfo = async (stationId) => {
    if (!stationId) return
    const res = await getStationInfo(stationId,reportDate.value)
    stationInfo.value = res?.data ?? {}
}
const overViewData = ref({})

/** 电量展示：与运营页一致，kWh → MWh/GWh */
function formatEnergyDisplay(num) {
    if (num == null || num === '' || (typeof num === 'number' && !Number.isFinite(num))) {
        return { value: '--', unit: '' }
    }
    const n = Number(num)
    if (Number.isNaN(n)) return { value: '--', unit: '' }
    let value = n
    let unit = 'kWh'
    if (n >= 1_000_000) {
        value = n / 1_000_000
        unit = 'GWh'
    } else if (n >= 1_000) {
        value = n / 1_000
        unit = 'MWh'
    }
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit
    }
}

function formatAmount(num, fractionMax = 4) {
    if (num == null || num === '' || (typeof num === 'number' && !Number.isFinite(num))) return '--'
    const n = Number(num)
    if (Number.isNaN(n)) return '--'
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: fractionMax })
}

/**
 * getOverview.data 结构：
 * - overview：累计充/放/收益等（累计收益=thisMonthIncome，总收益=sumIncome）
 * - IncomeDistribution：环形图 income / vppIncome / esmIncome / otherIncome
 * - incomeList：柱状图+明细表，每项 date + 同上四字段
 */
const overviewBlock = computed(() => {
    const d = overViewData.value
    if (!d || typeof d !== 'object') return {}
    const ov = d.overview
    return ov != null && typeof ov === 'object' ? ov : d
})

const overviewCumulativeCharge = computed(() => formatEnergyDisplay(overviewBlock.value.sumPositive))
const overviewCumulativeDischarge = computed(() => formatEnergyDisplay(overviewBlock.value.sumReverse))

const overviewUnitCapacityIncomeDisplay = computed(() => {
    const v = overviewBlock.value.unitCapacityIncome
    if (v == null || v === '') return '--'
    const n = Number(v)
    if (Number.isNaN(n)) return '--'
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 6 })
})

const overviewAvgCycleDisplay = computed(() => {
    const v = overviewBlock.value.avgCycleIndex
    if (v == null || v === '') return '--'
    const n = Number(v)
    if (Number.isNaN(n)) return '--'
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
})

/** 较上期：接口可为比例(0.02)或百分点(2)，null 不展示 */
const overviewComparedLastMonthTrend = computed(() => {
    const v = overviewBlock.value.comparedLastMonth
    if (v == null || v === '') return null
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    const pct = Math.abs(n) <= 1 ? n * 100 : n
    const sign = pct > 0 ? '增长' : '下降'
    return {
        text: `较上期${sign} ${Math.abs(pct).toFixed(1)}%`,
        cls: pct >= 0 ? 'up' : 'down'
    }
})

/** inFront：优先按「位于前 x%」展示（0~100）；否则展示排名 */
const overviewInFrontTrend = computed(() => {
    const v = overviewBlock.value.inFront
    if (v == null || v === '') return ''
    const n = Number(v)
    if (!Number.isFinite(n)) return ''
    if (n >= 0 && n <= 100) return `位于前 ${n}%`
    return `排名 ${n}`
})

const overviewCycleStandardTrend = computed(() => {
    const cycle = Number(overviewBlock.value.avgCycleIndex)
    const std = Number(overviewBlock.value.avgStandard)
    if (!Number.isFinite(cycle) || !Number.isFinite(std) || std === 0) return null
    const diffPct = ((cycle - std) / std) * 100
    if (diffPct < 0) {
        return { text: `低于标准 ${Math.abs(diffPct).toFixed(1)}%`, cls: 'down' }
    }
    if (diffPct > 0) {
        return { text: `高于标准 ${Math.abs(diffPct).toFixed(1)}%`, cls: 'up' }
    }
    return null
})

/** getOverview.data 与 overview 同级：IncomeDistribution（环形图）、incomeList（柱状图+明细表） */
const INCOME_DISTRIBUTION_KEYS = [
    { field: 'income', name: '峰谷套利', color: '#3A68FF' },
    { field: 'vppIncome', name: '虚拟电厂', color: '#4CAF50' },
    { field: 'esmIncome', name: '电力现货', color: '#FFC107' },
    { field: 'otherIncome', name: '其他收益', color: '#FF9800' }
]

const incomeDistributionBlock = computed(() => {
    const d = overViewData.value
    if (!d || typeof d !== 'object') return null
    return d.IncomeDistribution ?? d.incomeDistribution ?? null
})

const incomeListRaw = computed(() => {
    const d = overViewData.value
    const list = d?.incomeList ?? d?.income_list
    return Array.isArray(list) ? list : []
})

const incomePieLegendItems = computed(() => {
    const dist = incomeDistributionBlock.value
    if (!dist || typeof dist !== 'object') {
        return INCOME_DISTRIBUTION_KEYS.map((it) => ({
            name: it.name,
            pct: 0,
            amount: 0,
            color: it.color
        }))
    }
    const total = INCOME_DISTRIBUTION_KEYS.reduce(
        (s, it) => s + Number(dist[it.field] ?? 0),
        0
    )
    return INCOME_DISTRIBUTION_KEYS.map((it) => {
        const v = Number(dist[it.field] ?? 0)
        const pct = total > 0 ? Math.round((v / total) * 1000) / 10 : 0
        return { name: it.name, pct, amount: v, color: it.color }
    })
})

const incomePieOption = computed(() => {
    const items = incomePieLegendItems.value
    const total = items.reduce((s, it) => s + (it.amount || 0), 0)
    const data = items.map((it) => ({
        value: it.amount,
        name: it.name,
        itemStyle: { color: it.color }
    }))
    return {
        tooltip: {
            trigger: 'item',
            formatter: (p) => {
                if (!p || p.name == null) return ''
                const pct =
                    total > 0 && p.value != null
                        ? ((Number(p.value) / total) * 100).toFixed(1)
                        : '0.0'
                return `${p.name}<br/>${formatMoney(p.value).value} ${formatMoney(p.value).unit} (${pct}%)`
            }
        },
        series: [
            {
                type: 'pie',
                radius: ['55%', '75%'],
                center: ['50%', '50%'],
                label: {
                    show: true,
                    fontSize: 12,
                    formatter: (p) => (total > 0 ? `${p.percent.toFixed(1)}%` : '0%')
                },
                data
            }
        ]
    }
})

const incomeBarOption = computed(() => {
    const list = incomeListRaw.value
    const xData =
        list.length > 0
            ? list.map((row) =>
                row.date ? dayjs(row.date).format('MM-DD') : '--'
            )
            : ['--']
    const series = INCOME_DISTRIBUTION_KEYS.map((def) => ({
        name: def.name,
        type: 'bar',
        stack: 'income',
        barWidth: 14,
        data:
            list.length > 0
                ? list.map((row) => Number(row[def.field] ?? 0))
                : [0],
        itemStyle: { color: def.color }
    }))
    return {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: {
            data: INCOME_DISTRIBUTION_KEYS.map((d) => d.name),
            bottom: 0
        },
        grid: { top: 30, left: 40, right: 16, bottom: 40, containLabel: true },
        xAxis: {
            type: 'category',
            data: xData,
            axisLabel: { fontSize: 10 }
        },
        yAxis: {
            type: 'value',
            axisLabel: { fontSize: 10 },
            splitLine: { lineStyle: { color: 'rgba(0,0,0,0.08)' } },
            name: '收益(元)',
            nameLocation: 'end',
        },
        series
    }
})
// 换算收益单位
function formatMoney(num) {
    const n = Number(num ?? 0);

    if (isNaN(n)) return { value: '0.00', unit: '元' };

    const units = [
        { threshold: 100000000, divisor: 100000000, unit: '亿元' },
        { threshold: 10000, divisor: 10000, unit: '万元' }
    ];

    const { divisor, unit } = units.find(u => n >= u.threshold) || { divisor: 1, unit: '元' };
    return {
        value: (n / divisor).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit
    };
}
// 换算电量单位
function formatNumber(num) {
    if (num == null || isNaN(Number(num))) {
        return { value: '0.00', unit: '元' }
    }
    const n = Number(num)
    let value = n
    let unit = 'kWh'
    if (n >= 1_000_000) {
        value = n / 1_000_000
        unit = 'GWh'
    } else if (n >= 1_000) {
        value = n / 1_000
        unit = 'MWh'
    }
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2 }),
        unit: unit
    }
}

/** 从对象上按候选 key 取第一个数组字段（chargeRecords 多形态兼容） */
function firstArrayByKeys(obj, keys) {
    if (!obj || typeof obj !== 'object') return []
    for (const k of keys) {
        const v = obj[k]
        if (Array.isArray(v)) return v
    }
    return []
}

/** 单格 eBlock 充电记录 → 三列展示值 */
function formatEBlockSlotCells(item) {
    if (!item) {
        return { time: '--', energy: '--', soc: '--' }
    }
    const st = item.startTime ?? item.start_time ?? ''
    const et = item.endTime ?? item.end_time ?? ''
    const time = st && et ? `${st} - ${et}` : '--'
    const etotal = item.energyTotal ?? item.energy_total
    const energy =
        etotal != null && etotal !== ''
            ? Number(etotal).toLocaleString('zh-CN', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
            : '--'
    const socRaw = item.endSoc ?? item.end_soc
    const soc = socRaw != null && socRaw !== '' ? String(Number(socRaw)) : '--'
    return { time, energy, soc }
}

/** 收益统计明细：getChargeRecords */
const chargeRecordsData = ref({})

const eBlockStatBlockNames = computed(() =>
    firstArrayByKeys(chargeRecordsData.value, ['eBlockNameList', 'e_blockNameList', 'e_block_name_list'])
)

/** 当前 reportDate：各 eBlock 按数组下标对齐成行 */
const incomeEBlockStatRows = computed(() => {
    const names = eBlockStatBlockNames.value
    const dateStr = reportDate.value
    const dataArr = firstArrayByKeys(chargeRecordsData.value, ['eBlockData', 'e_blockData', 'e_block_data'])
    if (!names.length) return []

    const dayRecord = dataArr.find((d) => d && String(d.date).slice(0, 10) === dateStr)
    if (!dayRecord) return []

    const lengths = names.map((n) => (Array.isArray(dayRecord[n]) ? dayRecord[n].length : 0))
    const maxLen = Math.max(0, ...lengths)
    if (!maxLen) return []

    return Array.from({ length: maxLen }, (_, i) => {
        const row = { date: dateStr }
        names.forEach((name, bi) => {
            const arr = dayRecord[name]
            const cells = formatEBlockSlotCells(Array.isArray(arr) ? arr[i] : null)
            row[`b${bi}_time`] = cells.time
            row[`b${bi}_energy`] = cells.energy
            row[`b${bi}_soc`] = cells.soc
        })
        return row
    })
})

function eBlockIncomeStatSpanMethod({ rowIndex, columnIndex }) {
    const total = incomeEBlockStatRows.value.length
    if (!total) return { rowspan: 1, colspan: 1 }
    if (columnIndex !== 0) return { rowspan: 1, colspan: 1 }
    if (rowIndex === 0) return { rowspan: total, colspan: 1 }
    return { rowspan: 0, colspan: 0 }
}

function stationDateParams(stationId, date) {
    return { stationId, date }
}

const fetchOverViewData = async (stationId, date) => {
    if (!stationId) return
    const res = await getOverview(stationDateParams(stationId, date))
    overViewData.value = res?.data ?? {}
}
const chargeSummaryData = ref({})
const fetchChargeSummaryData = async (stationId, date) => {
    if (!stationId) return
    const res = await getChargeSummary(stationDateParams(stationId, date))
    chargeSummaryData.value = res?.data ?? {}
}

const fetchChargeRecordsData = async (stationId, date) => {
    if (!stationId) return
    const res = await getChargeRecords(stationDateParams(stationId, date))
    chargeRecordsData.value = res?.data ?? {}
}

const fetchLinesOptionData = async (stationId, date) => {
    if (stationId == null || stationId === '' || date == null || String(date).trim() === '') return
    const res = await queryLinesOption(stationId, date)
    linesOptionData.value = {
        actualDemand: Array.isArray(res.actualDemand) ? res.actualDemand : [],
        energyDemand: Array.isArray(res.energyDemand) ? res.energyDemand : [],
        loadDemand: Array.isArray(res.loadDemand) ? res.loadDemand : [],
        cycle: res.cycle ?? null,
        slip: res.slip ?? null
    }
}
const dayIncomeNum = ref(0)
const dayChargeNum = ref(0)
const dayDischargeNum = ref(0)
const fetchDailySummaryData = async (stationId, date) => {
    if (!stationId) return
    const res = await getDailySummary(stationDateParams(stationId, date))
    dailySummaryData.value = res?.data ?? {}
    incomeDetailMeterTab.value = '0'
    dayIncomeNum.value = dailySummaryData.value?.sumIncome ?? 0
    dayChargeNum.value = dailySummaryData.value?.sumPositive ?? 0
    dayDischargeNum.value = dailySummaryData.value?.sumReverse ?? 0
}




/** 抽屉内：按站点 + 报表日期拉取全套日报接口（顺序与依赖无关，保持原顺序） */
async function loadReportDetailForDrawer(stationId, date) {
    reportDetailLoading.value = true
    try {
        deviceAlarmsPage.value = 1
        deviceAlarmsClientFullList.value = null
        await fetchOverViewData(stationId, date)
        await fetchChargeSummaryData(stationId, date)
        await fetchLinesOptionData(stationId, date)
        await fetchChargeRecordsData(stationId, date)
        await fetchDailySummaryData(stationId, date)
        await fetchAlertList(stationId, date, 1)
        await fetchStationInfo(stationId, date)
    } finally {
        reportDetailLoading.value = false
    }
}

watch(
    [() => props.visible, () => props.row?.stationId],
    async ([visible, stationId]) => {
        if (!visible || !stationId) return

        reportDate.value = dayjs().subtract(1, 'day').format('YYYY-MM-DD')
        customDate.value = reportDate.value
        showCustomDate.value = false

        if (lastLoadedStationId.value !== stationId || !Object.keys(stationInfo.value || {}).length) {
            await fetchStationInfo(stationId, reportDate.value)
            lastLoadedStationId.value = stationId
        }

        await loadReportDetailForDrawer(stationId, reportDate.value)
    },
    { immediate: true }
)

const onQueryCustomDate = async () => {
    if (!props.visible || !props.row?.stationId || !customDate.value) return
    reportDate.value = customDate.value
    
    await loadReportDetailForDrawer(props.row.stationId, reportDate.value)
    showCustomDate.value = false
}

</script>

<style scoped lang="scss">
.day-detail {
    padding: 8px 4px 16px;
}

.day-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    width: 100%;
    min-width: 0;
}

.day-drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
    white-space: nowrap;
}

.day-drawer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.day-drawer-date-text {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.65);
    white-space: nowrap;
}

.day-date-btn {
    color: rgba(58, 104, 255, 0.9);
}

.day-date-picker {
    width: 190px;
}

.day-section-title {
    margin: 0 0 12px 0;
    font-size: 15px;
    font-weight: 600;
    color: #2e2e2e;
}

.day-income-subtitle {
    margin: 18px 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #2e2e2e;
}

.day-income-meter-tabs {
    margin: -4px 0 8px 0;
}

.day-income-meter-tabs :deep(.el-tabs__header) {
    margin-bottom: 8px;
}

.day-income-meter-tabs :deep(.el-tabs__item) {
    font-size: 13px;
    padding: 0 16px;
    height: 36px;
    line-height: 36px;
}

/* 仅用电表名称作切换，表格在 tab 外，隐藏内容区 */
.day-income-meter-tabs :deep(.el-tabs__content) {
    display: none;
}

.day-basic-table {
    margin-bottom: 20px;
    font-size: 13px;
}

.day-cards-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
}

.day-card {
    flex: 1;
    min-width: 0;
    border-radius: 12px;
    padding: 16px 18px;
    background: linear-gradient(135deg, #f6f9ff 0%, #ffffff 100%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.day-card-label {
    font-size: 13px;
    color: rgba(46, 46, 46, 0.6);
    margin-bottom: 8px;
}

.day-card-value {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.day-card-value .num {
    font-size: 22px;
    font-weight: 700;
    color: #3A68FF;
}

.day-card-value .unit {
    font-size: 13px;
    color: rgba(46, 46, 46, 0.6);
}

.day-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 8px 0 12px;
    align-items: stretch;
}

.day-summary-grid.second-row {
    margin-top: 0;
}

.day-summary-card {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.day-summary-card .icon.placeholder {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #f0f4ff, #e0edff);
    margin-right: 12px;
}

.day-summary-card .meta .label {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
    margin-bottom: 4px;
}

.day-summary-card .meta .value {
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
}

.day-summary-card .meta .value .unit {
    font-size: 12px;
    margin-left: 4px;
}

.day-summary-card .trend {
    margin-left: 8px;
    font-size: 12px;
}

.day-summary-card .trend.up {
    color: #1ab65c;
}

.day-summary-card .trend.down {
    color: #e53935;
}

.day-summary-card-sum-income .meta .value {
    font-size: 20px;
    font-weight: 800;
    color: #1f2937;
}

.day-summary-card-month-income .meta .value {
    font-size: 20px;
    font-weight: 800;
    color: #1f2937;
}

.day-summary-card-month-income .trend {
    margin-left: 10px;
    font-size: 12px;
    font-weight: 800;
}

.day-summary-card-month-income .trend.up::before {
    content: '▲';
    display: inline-block;
    margin-right: 6px;
}

.day-summary-card-month-income .trend.down::before {
    content: '▼';
    display: inline-block;
    margin-right: 6px;
}

.day-section-sub {
    margin: 0 0 12px 0;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.55);
}

.day-analysis-row {
    display: flex;
    gap: 16px;
    margin: 16px 0 20px;
}

.day-chart-card {
    flex: 1;
    border-radius: 12px;
    padding: 12px 16px 16px;
    background: #ffffff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.day-chart-title {
    font-size: 13px;
    font-weight: 600;
    color: #2e2e2e;
    margin-bottom: 8px;
}

.day-pie-chart {
    height: 160px;
}

.day-bar-chart {
    height: 180px;
}

.day-pie-legend {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.8);
}

.day-pie-legend li {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.day-pie-legend .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.day-pie-legend .name {
    flex: 1;
}

.day-pie-legend .pct {
    color: rgba(46, 46, 46, 0.6);
}

.day-stat-table {
    margin-top: 12px;
    font-size: 13px;
}

.day-eblock-stat-table :deep(.el-table__header-wrapper th.el-table__cell) {
    background: #f5f7fa;
}

.eblock-soc-cell {
    color: #16a34a;
    font-weight: 600;
}

.day-income-table {
    margin-top: 12px;
    font-size: 13px;
}

.day-demand-card {
    margin-top: 20px;
}

.day-chart-sub {
    margin-left: 8px;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.6);
}

.day-demand-chart {
    height: 220px;
}

.day-alarm-table {
    margin: 12px 0 8px;
    font-size: 13px;
}

.day-alarm-pagination {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

/* 设备告警分页：沿用 ChargeRecord.vue 的分页样式 */
.pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background: #fff;
    flex-shrink: 0;
    border-top: 1px solid #f0f0f0;
    width: 100%;

    :deep(.v-btn--disabled) {
        color: #fff !important;
        opacity: 1 !important;
    }
}

.pagination-info {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
}

.page-size-select {
    width: 112px;
    flex-shrink: 0;

    :deep(.v-field) {
        background: #fff !important;
        border: 1px solid #dcdcdc !important;
        border-radius: 3px !important;
        min-height: auto !important;
        height: auto !important;
        padding: 5px 8px !important;
        box-shadow: none !important;
        display: flex !important;
        align-items: center !important;
    }

    :deep(.v-field__input) {
        padding: 0 !important;
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        color: rgba(0, 0, 0, 0.9) !important;
        min-height: auto !important;
        height: 22px !important;
        line-height: 22px !important;
        display: flex !important;
        align-items: center !important;
    }

    :deep(.v-select__selection) {
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        color: rgba(0, 0, 0, 0.9) !important;
        line-height: 22px !important;
        margin: 0 !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    :deep(.v-field__outline) {
        display: none !important;
    }

    :deep(.v-field__append-inner .v-icon) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
        color: #2E2E2E !important;
        opacity: 0.22;
    }
}

.custom-pagination {
    flex-shrink: 0;

    :deep(.v-pagination__list) {
        align-items: center;
        gap: 8px !important;
        margin: 0 !important;
    }

    :deep(.v-btn) {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        border-radius: 3px !important;
        margin: 0 !important;
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 22px !important;
        border: none !important;
        background: transparent !important;
        color: #000000E6 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0 !important;
        transition: none !important;
        box-shadow: none !important;
        text-align: center !important;

        &:hover {
            background: transparent !important;
            color: #000000E6 !important;
        }

        &.v-pagination__item--is-active {
            color: #000000E6 !important;
            border: none !important;
            box-shadow: none !important;
        }
    }

    :deep(.v-pagination__prev),
    :deep(.v-pagination__next) {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        border-radius: 3px !important;
        border: none !important;
        background: transparent !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: none !important;
        box-shadow: none !important;

        &:hover:not(.v-pagination__item--disabled) {
            background: transparent !important;
        }

        &.v-pagination__item--disabled {
            opacity: 0.4 !important;
            cursor: not-allowed !important;
        }
    }

    :deep(.v-pagination__prev .v-icon),
    :deep(.v-pagination__next .v-icon) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
        color: rgba(0, 0, 0, 1) !important;
    }
}

.jump-to-page {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
    background: #e3e3e3;
    padding: 2px 16px;
    border-radius: 3px;
    line-height: 22px;
    height: 34px;
    width: 160px;
    flex-shrink: 0;

    span {
        white-space: nowrap;
        line-height: 22px;
        font-weight: 400;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.6);
    }

    .jump-input {
        flex: 1 0 0;
        min-width: 0;

        :deep(.v-field) {
            background: #fff !important;
            border: 1px solid #dcdcdc !important;
            border-radius: 3px !important;
            min-height: auto !important;
            height: auto !important;
            margin: 0 !important;
            box-shadow: none !important;
            display: flex !important;
            align-items: center !important;
        }
    }
}

.day-advise-list {
    list-style: none;
    margin: 4px 0 16px;
    padding: 0;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.85);
}

.day-advise-list li {
    margin-bottom: 4px;
}

.tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    margin-right: 6px;
}

.tag.low {
    background: #E3F2FD;
    color: #1976D2;
}

.tag.mid {
    background: #FFF8E1;
    color: #FFA000;
}

.tag.high {
    background: #FFEBEE;
    color: #D32F2F;
}

.day-risk-card {
    padding: 16px 18px;
    border-radius: 12px;
    background: #F4FAFF;
    border: 1px solid rgba(58, 104, 255, 0.15);
}

.day-risk-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.day-risk-header .title {
    font-size: 14px;
    font-weight: 600;
    color: #2e2e2e;
}

.day-risk-desc {
    margin: 0 0 4px;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.8);
}
</style>
